import React from 'react';
import { Link } from 'gatsby';
import dayjs from 'dayjs';
import { Badge, ListGroupItem, Button } from 'reactstrap';
import classnames from 'classnames';

import { toJobSearchJobDetail } from '../../routes';

import {
  formatSalary,
  positionCodeToDisplayName,
  specialtyOptions,
} from '../../utils/displayHelper';
import { excludedSpecialties } from '../../utils/displayHelper';
import { urlTitle } from '../../utils/browserHelper';

import ProfilePicture from '../common/ProfilePicture';

import {
  FaLock,
  FaEye,
  FaCheck,
  FaEnvelope,
  FaMapMarkedAlt,
  FaChevronRight,
} from 'react-icons/fa';
import types from '../../types/services-api';
import relativeTime from 'dayjs/plugin/relativeTime';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import {
  isStaffType,
  UserContext,
  UserContextType,
} from '../../context/UserContext';
import { useContext, useState } from 'react';
import {
  isCsvJob,
  isPartnerCsvJob,
  isDpAuthJob,
  isPartnerAuthJob,
  JobSearchContext,
} from '../../context/JobSearchContext';
import {
  JobPostingCSVJobType,
  JobPostingCSVPartnerType,
} from '../../types/csv';
import OffcanvasJobView from '../job-search/OffcanvasJobView';
import JobPostEvaluation from '../common/JobPostEvaluation';
dayjs.extend(customParseFormat);
dayjs.extend(relativeTime);

const JobPostingListItem = (props: {
  job:
    | types.JobInternalViewType
    | types.PartnerJobDetailViewType
    | JobPostingCSVJobType
    | JobPostingCSVPartnerType;
}) => {
  const { updatePremiumJob } = useContext(JobSearchContext);
  const [job, setJob] = useState(props.job);

  const [offCanvasJob, setOffCanvasJob] = useState<string | undefined>(
    undefined
  );
  function toggleOffcanvasJob(jobId?: string) {
    if (!!jobId) {
      setOffCanvasJob(jobId);
    } else {
      setOffCanvasJob(undefined);
    }
  }

  const { user } = useContext(UserContext) as UserContextType;
  if (isCsvJob(job)) {
    const daysAgo = dayjs().diff(dayjs(job.updatedDate, 'YYYY-MM-DD'), 'days');
    return (
      <ListGroupItem className={'rounded mb-3 border border-light'}>
        <div className="d-flex gap-3 flex-nowrap">
          <div
            style={{ width: '55px', height: '55px' }}
            className="d-flex justify-content-center align-items-center"
          >
            <ProfilePicture
              isThirdParty={false}
              alt={'DentalPost'}
              className="img-fluid"
            />
          </div>
          <div>
            <p className="mb-0 lead fw-bolder">{job.title}</p>
            <p className="fw-bolder mb-0">
              {job.isConfidential === 'true' ? (
                <span>
                  <Badge color="light" className="text-dark">
                    <FaLock /> Confidential
                  </Badge>
                </span>
              ) : (
                <span className="d-block">{job.name}</span>
              )}
            </p>
            <p className="mb-0">
              <small>
                {job.city}, {job.state} {job.zip}
              </small>
            </p>
          </div>
        </div>
        <div>
          <hr className="my-1" />
          <p className="mb-0">
            <span className="fw-bold">Posted: </span>
            <span>
              {daysAgo <= 2 && <span className="text-primary">New!</span>}
              {daysAgo === 0 && 'Today'}
              {daysAgo > 0 && (
                <>
                  {daysAgo} {daysAgo === 1 ? 'day' : 'days'} ago
                </>
              )}
            </span>
          </p>
          <p className="mb-0">
            <span className="fw-bold">Compensation: </span>
            <span>
              {!!job.salary
                ? formatSalary(job.salary, job.salaryUnits)
                : 'Not Provided'}
            </span>
          </p>
          {job.jobType === 'PLANNED' ? (
            <p className="mb-0 d-flex">
              <span className="fw-bold text-primary">Temp Dates: </span>
              <ul style={{ listStyle: 'none' }} className="ps-2">
                {job.jobDates.split(',').map((day) => {
                  return (
                    <li key={day}>
                      <small>{dayjs(day).format('MMMM DD, YYYY')}</small>
                    </li>
                  );
                })}
              </ul>
            </p>
          ) : (
            <p className="mb-0">
              <span className="fw-bold">Schedule: </span>
              <span>{job.dow.replaceAll(',', ', ')}</span>
            </p>
          )}
        </div>
        <div className="d-flex gap-4 justify-content-between align-items-center">
          <div>
            {job.isNational && (
              <p className="mb-0">
                <small className="fw-bold text-secondary">
                  <FaMapMarkedAlt /> Nationally Advertised
                </small>
              </p>
            )}
          </div>
        </div>
        <hr />
        <div className="d-flex gap-4 justify-content-between my-2">
          <Button
            className="ms-auto"
            color="primary"
            outline
            tag={Link}
            to={`/dental-jobs${job.uri}`}
          >
            View Job <FaChevronRight />
          </Button>
        </div>
      </ListGroupItem>
    );
  }
  if (isPartnerCsvJob(job)) {
    const daysAgo = dayjs().diff(dayjs(job.postedAt, 'YYYY-MM-DD'), 'days');
    return (
      <>
        <ListGroupItem className={'rounded mb-3 border border-light'}>
          <div className="d-flex gap-3 flex-nowrap">
            <div
              style={{ width: '55px', height: '55px' }}
              className="d-flex justify-content-center align-items-center"
            >
              <ProfilePicture
                isThirdParty={true}
                alt={job.source}
                className="img-fluid"
              />
            </div>
            <div>
              <p className="mb-0 lead fw-bolder">{job.title}</p>
              <p className="fw-bolder mb-0">
                <span className="d-block">{job.company}</span>
              </p>
              <p className="mb-0">
                <small>
                  {job.city}, {job.state} {job.zip}
                </small>
              </p>
            </div>
          </div>
          <div>
            <hr className="my-1" />
            <p className="mb-0">
              <span className="fw-bold">Posted: </span>
              <span>
                {daysAgo <= 2 && <span className="text-primary">New!</span>}
                {daysAgo === 0 && 'Today'}
                {daysAgo > 0 && (
                  <>
                    {daysAgo} {daysAgo === 1 ? 'day' : 'days'} ago
                  </>
                )}
              </span>
            </p>
            <p className="mb-0">
              <span className="fw-bold">Compensation: </span>
              <span>{!!job.salary ? job.salary : 'Not Provided'}</span>
            </p>
            <p className="mb-0">
              <span className="fw-bold">Source: </span>
              <span style={{ textTransform: 'capitalize' }}>{job.source}</span>
            </p>
          </div>
          <hr />
          <div className="d-flex justify-content-end my-2">
            <Button
              color="primary"
              outline
              tag={Link}
              to={'#'}
              onClick={() => toggleOffcanvasJob(job.jobKey)}
            >
              View Job <FaChevronRight />
            </Button>
          </div>
        </ListGroupItem>
        <OffcanvasJobView toggle={toggleOffcanvasJob} jobId={offCanvasJob} />
      </>
    );
  } else if (isDpAuthJob(job)) {
    const daysAgo = dayjs().diff(dayjs(job.updatedDate, 'YYYY-MM-DD'), 'days');

    const adjustedPosition =
      (!!job.specialty && !excludedSpecialties.includes(job.specialty)
        ? specialtyOptions[job.specialty] || job.specialty
        : '') +
      ' ' +
      positionCodeToDisplayName[job.position];

    const Title = () => {
      return (
        <p className="mb-0 lead fw-bolder">
          {job.specialty && !excludedSpecialties.includes(job.specialty)
            ? specialtyOptions[job.specialty] || job.specialty
            : ''}{' '}
          {job.position ? positionCodeToDisplayName[job.position] : ''}
        </p>
      );
    };

    return (
      <ListGroupItem
        className={classnames('rounded mb-3 border border-light', {
          'bg-white text-dark': !job.viewedDate,
          // 'bg-light text-dark': !!job.viewedDate && !job.appliedDate,
          // 'dp-bg-success-light text-dark': !!job.appliedDate,
        })}
      >
        <div className="d-flex gap-3 flex-nowrap">
          <div
            style={{ minWidth: '55px', maxWidth: '55px' }}
            className="d-flex justify-content-center align-items-center"
          >
            <img
              src={job.photoUrl}
              alt={job.employerName || 'employer'}
              className="img-fluid"
            />
          </div>
          <div>
            <Title />
            <p className="fw-bolder mb-0">
              {job.isConfidential ? (
                <span>
                  <Badge color="light" className="text-dark">
                    <FaLock /> Confidential
                  </Badge>
                </span>
              ) : (
                <span className="d-block">{job.employerName}</span>
              )}
            </p>
            <p className="mb-0">
              <small>
                {job.city}, {job.state} {job.zip}{' '}
                {!!job.distance && job.distance < 200 && (
                  <em>({job.distance} miles)</em>
                )}
              </small>
            </p>
          </div>
        </div>
        <div>
          <hr className="my-1" />
          <p className="mb-0">
            <span className="fw-bold">Posted: </span>
            <span>
              {daysAgo <= 2 && !job.viewedDate && (
                <span className="text-primary">New!</span>
              )}
              {daysAgo === 0 && 'Today'}
              {daysAgo > 0 && (
                <>
                  {daysAgo} {daysAgo === 1 ? 'day' : 'days'} ago
                </>
              )}
            </span>
          </p>
          <p className="mb-0">
            <span className="fw-bold">Compensation: </span>
            <span>
              {!!job.salary
                ? formatSalary(job.salary, job.salaryUnits)
                : 'Not Provided'}
            </span>
          </p>
          {job.isPlanned ? (
            <p className="mb-0 d-flex">
              <span className="fw-bold text-primary">Temp Dates: </span>
              <ul style={{ listStyle: 'none' }} className="ps-2">
                {job.jobDates.map((day) => {
                  return (
                    <li key={day.date ?? day}>
                      <small>
                        {dayjs(day.date ?? day).format('MMMM DD, YYYY')}
                      </small>
                    </li>
                  );
                })}
              </ul>
            </p>
          ) : (
            <p className="mb-0">
              <span className="fw-bold">Schedule: </span>
              <span>{job.dow.replaceAll(',', ', ')}</span>
            </p>
          )}
        </div>
        <div className="d-flex gap-4 justify-content-between align-items-center">
          <div>
            {job.isNational && (
              <p className="mb-0">
                <small className="fw-bold text-secondary">
                  <FaMapMarkedAlt /> Nationally Advertised
                </small>
              </p>
            )}
            {!!job.invitedDate && !job.appliedDate && (
              <p className="mb-0">
                <small className="fw-bold text-secondary">
                  <FaEnvelope /> Invited to apply{' '}
                  {dayjs(job.invitedDate).format('MMMM DD')}
                </small>
              </p>
            )}
            {!!job.viewedDate && !job.appliedDate && (
              <small className="fw-bold text-muted">
                <FaEye /> Viewed{' '}
                {dayjs(job.viewedDate).format(
                  `MMMM D${
                    dayjs().diff(dayjs(job.viewedDate), 'year') > 0
                      ? ', YYYY'
                      : ''
                  }`
                )}
              </small>
            )}
            {job.appliedDate && (
              <small className="text-success fw-bold">
                <FaCheck /> Application sent{' '}
                {dayjs(job.appliedDate).format(
                  `MMMM D${
                    dayjs().diff(dayjs(job.appliedDate), 'year') > 0
                      ? ', YYYY'
                      : ''
                  }`
                )}
              </small>
            )}
          </div>
        </div>
        <hr />
        <div className="d-flex gap-4 justify-content-between my-2">
          {isDpAuthJob(props.job) && isStaffType(user) && (
            <JobPostEvaluation
              job={job}
              updateJob={(updatedJob) => {
                setJob(updatedJob);
                updatePremiumJob(updatedJob);
              }}
            />
          )}
          <Button
            className="ms-auto"
            color="primary"
            outline
            tag={Link}
            to={toJobSearchJobDetail(
              job.id,
              urlTitle({
                position: adjustedPosition,
                city: job.city,
                state: job.state,
              })
            )}
          >
            View Job <FaChevronRight />
          </Button>
        </div>
      </ListGroupItem>
    );
  } else if (isPartnerAuthJob(job)) {
    const daysAgo = dayjs().diff(dayjs(job.postedAt, 'YYYY-MM-DD'), 'days');

    const adjustedPosition = !!job.position
      ? positionCodeToDisplayName[job.position]
      : '';

    return (
      <ListGroupItem
        className={classnames('rounded mb-3 border border-light', {
          'bg-white text-dark': !job.isViewed,
        })}
      >
        <div className="d-flex gap-3 flex-nowrap">
          <div
            style={{ width: '55px', height: '55px' }}
            className="d-flex justify-content-center align-items-center"
          >
            <ProfilePicture
              isThirdParty={true}
              alt={job.source}
              className="img-fluid"
            />
          </div>
          <div>
            <p className="mb-0 lead fw-bolder">{job.title}</p>
            <p className="fw-bolder mb-0">
              <span className="d-block">{job.company}</span>
            </p>
            <p className="mb-0">
              <small>
                {job.city}, {job.state} {job.zip}{' '}
                {!!job.distance && job.distance < 200 && (
                  <em>({job.distance} miles)</em>
                )}
              </small>
            </p>
          </div>
        </div>
        <div>
          <hr className="my-1" />
          <p className="mb-0">
            <span className="fw-bold">Posted: </span>
            <span>
              {daysAgo <= 2 && !job.isViewed && (
                <span className="text-primary">New!</span>
              )}
              {daysAgo === 0 && 'Today'}
              {daysAgo > 0 && (
                <>
                  {daysAgo} {daysAgo === 1 ? 'day' : 'days'} ago
                </>
              )}
            </span>
          </p>
          <p className="mb-0">
            <span className="fw-bold">Compensation: </span>
            <span>{job.salary}</span>
          </p>
          <p className="mb-0">
            <span className="fw-bold">Source: </span>
            <span style={{ textTransform: 'capitalize' }}>{job.source}</span>
          </p>
        </div>
        <div className="d-flex gap-4 justify-content-between align-items-center">
          <div>
            {job.isViewed && (
              <small className="fw-bold text-muted">
                <FaEye /> Viewed{' '}
              </small>
            )}
          </div>
        </div>
        <hr />
        <div className="d-flex justify-content-end my-2">
          <Button
            color="primary"
            outline
            tag={Link}
            to={toJobSearchJobDetail(
              job.jobKey,
              urlTitle({
                position: adjustedPosition,
                city: job.city,
                state: job.state,
              })
            )}
          >
            View Job <FaChevronRight />
          </Button>
        </div>
      </ListGroupItem>
    );
  } else {
    return null;
  }
};

export default JobPostingListItem;
